import { canonicalizeAgentControllerRef, conversationExecutionStateSchema, parseAgentControllerRef, runtimeTransportModeSchema, } from "@openmeld/schemas";
export const LEDGER_STATE_VERSION = 2;
const EXECUTION_CONTEXT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const LEDGER_STATE_INVALID_JSON_ERROR = "ledger.state.invalid_json";
export const LEDGER_STATE_SCHEMA_ERROR = "ledger.state.schema_invalid";
export const LEDGER_STATE_BACKUP_ERROR = "ledger.state.backup_failed";
const UTF8_ENCODING = "utf8";
const EMPTY_PROMISE = Promise.resolve();
const historyWriteQueueByPath = new Map();
const stateWriteQueueByPath = new Map();
const historySizeByPath = new Map();
const HISTORY_ROTATION_MAX_BYTES = 4 * 1024 * 1024;
const HISTORY_ROTATION_MAX_ARCHIVES = 3;
const DEDUPE_RETENTION_MS = 48 * 60 * 60 * 1000;
const INFLIGHT_RETENTION_MS = DEDUPE_RETENTION_MS;
const AGENT_CONTEXT_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const AGENT_CONTEXT_MAX_ENTRIES = 2000;
const AGENT_CONTEXT_LOOKUP_KEY_SEPARATOR = "|";
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const isPositiveInteger = (value) => Number.isInteger(value) && typeof value === "number" && value >= 0;
const isRuntimeTransportMode = (value) => runtimeTransportModeSchema.safeParse(value).success;
const utf8ByteLength = (value) => {
    let byteLength = 0;
    for (const char of value) {
        const codePoint = char.codePointAt(0) ?? 0;
        if (codePoint <= 0x7f) {
            byteLength += 1;
            continue;
        }
        if (codePoint <= 0x7_ff) {
            byteLength += 2;
            continue;
        }
        if (codePoint <= 0xff_ff) {
            byteLength += 3;
            continue;
        }
        byteLength += 4;
    }
    return byteLength;
};
const toErrorCode = (error) => {
    if (!isRecord(error)) {
        return null;
    }
    const code = error.code;
    if (typeof code !== "string") {
        return null;
    }
    return code;
};
const encodeAgentContextLookupKeyPart = (value) => encodeURIComponent(value);
const buildStoredAgentContextLookupKey = (input) => [
    ...(input.executionContextId
        ? ["execution-context", input.executionContextId]
        : []),
    input.targetProfileId,
    input.agentControllerRef,
    input.transportMode,
    input.runtimeWorkspaceFingerprint,
]
    .map(encodeAgentContextLookupKeyPart)
    .join(AGENT_CONTEXT_LOOKUP_KEY_SEPARATOR);
const buildLegacySpaceScopedAgentContextLookupKey = (input) => [
    input.spaceId,
    input.targetProfileId,
    input.agentControllerRef,
    input.transportMode,
    input.runtimeWorkspaceFingerprint,
]
    .map(encodeAgentContextLookupKeyPart)
    .join(AGENT_CONTEXT_LOOKUP_KEY_SEPARATOR);
const parseInflightTask = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const { bindingKey, firstSeenAtMs, idempotencyKey, output, sequence, taskId, } = value;
    if (typeof taskId !== "string" ||
        taskId.length === 0 ||
        typeof bindingKey !== "string" ||
        bindingKey.length === 0 ||
        typeof idempotencyKey !== "string" ||
        idempotencyKey.length === 0 ||
        !isPositiveInteger(sequence) ||
        !isPositiveInteger(firstSeenAtMs)) {
        return null;
    }
    if (output !== undefined &&
        (typeof output !== "string" || output.length === 0)) {
        return null;
    }
    return {
        bindingKey,
        firstSeenAtMs,
        idempotencyKey,
        ...(output ? { output } : {}),
        sequence,
        taskId,
    };
};
const parseAgentContextState = (agentContextLookupKey, value) => {
    if (!isRecord(value)) {
        return null;
    }
    const { agentControllerConversationId, agentControllerRef, createdAtMs, lastTaskId, spaceId, targetProfileId, transportMode, runtimeWorkspaceFingerprint, updatedAtMs, } = value;
    const parsedAgentControllerRef = typeof agentControllerRef === "string"
        ? parseAgentControllerRef(agentControllerRef)
        : null;
    if (!isRuntimeTransportMode(transportMode) ||
        typeof agentContextLookupKey !== "string" ||
        agentContextLookupKey.length === 0 ||
        typeof spaceId !== "string" ||
        spaceId.length === 0 ||
        typeof targetProfileId !== "string" ||
        targetProfileId.length === 0 ||
        !parsedAgentControllerRef ||
        typeof runtimeWorkspaceFingerprint !== "string" ||
        runtimeWorkspaceFingerprint.length === 0 ||
        typeof agentControllerConversationId !== "string" ||
        agentControllerConversationId.length === 0 ||
        !isPositiveInteger(createdAtMs) ||
        !isPositiveInteger(updatedAtMs) ||
        typeof lastTaskId !== "string" ||
        lastTaskId.length === 0) {
        return null;
    }
    let executionContextId;
    if (agentContextLookupKey.startsWith("execution-context|")) {
        executionContextId = agentContextLookupKey.split("|")[1];
        if (!(executionContextId &&
            EXECUTION_CONTEXT_ID_PATTERN.test(executionContextId))) {
            return null;
        }
    }
    const canonicalLookupKey = buildStoredAgentContextLookupKey({
        executionContextId,
        agentControllerRef: parsedAgentControllerRef.ref,
        runtimeWorkspaceFingerprint,
        targetProfileId,
        transportMode,
    });
    const legacySpaceScopedLookupKey = buildLegacySpaceScopedAgentContextLookupKey({
        agentControllerRef: parsedAgentControllerRef.ref,
        runtimeWorkspaceFingerprint,
        spaceId,
        targetProfileId,
        transportMode,
    });
    if (agentContextLookupKey !== canonicalLookupKey &&
        agentContextLookupKey !== legacySpaceScopedLookupKey) {
        return null;
    }
    return {
        agentControllerConversationId,
        agentControllerRef: parsedAgentControllerRef.ref,
        createdAtMs,
        lastTaskId,
        agentContextLookupKey,
        spaceId,
        targetProfileId,
        transportMode,
        runtimeWorkspaceFingerprint,
        updatedAtMs,
    };
};
const parseAgentContextLifecycleCounters = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const { created, guardBlocked, recreated, reused } = value;
    if (!(isPositiveInteger(created) &&
        isPositiveInteger(recreated) &&
        isPositiveInteger(reused) &&
        isPositiveInteger(guardBlocked))) {
        return null;
    }
    return {
        created,
        guardBlocked,
        recreated,
        reused,
    };
};
const createEmptyAgentContextLifecycleCounters = () => ({
    created: 0,
    guardBlocked: 0,
    recreated: 0,
    reused: 0,
});
const parseAgentContextRotationRecord = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const { nextAgentControllerConversationId, previousAgentControllerConversationId, rotationAt, rotationReason, rotationTaskId, rotationTriggerErrorCode, } = value;
    if (typeof previousAgentControllerConversationId !== "string" ||
        previousAgentControllerConversationId.length === 0 ||
        typeof nextAgentControllerConversationId !== "string" ||
        nextAgentControllerConversationId.length === 0 ||
        typeof rotationTriggerErrorCode !== "string" ||
        rotationTriggerErrorCode.length === 0 ||
        typeof rotationReason !== "string" ||
        rotationReason.length === 0 ||
        !isPositiveInteger(rotationAt) ||
        typeof rotationTaskId !== "string" ||
        rotationTaskId.length === 0) {
        return null;
    }
    return {
        nextAgentControllerConversationId,
        previousAgentControllerConversationId,
        rotationAt,
        rotationReason,
        rotationTaskId,
        rotationTriggerErrorCode,
    };
};
const parseAgentContextRotationsOrDefault = (value) => {
    if (value === undefined) {
        return [];
    }
    if (!Array.isArray(value)) {
        return null;
    }
    const nextRecords = [];
    for (const item of value) {
        const parsed = parseAgentContextRotationRecord(item);
        if (!parsed) {
            return null;
        }
        nextRecords.push(parsed);
    }
    return nextRecords;
};
const parseDedupeFirstSeenByKey = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const nextDedupeFirstSeenByKey = {};
    for (const [key, firstSeenAtMs] of Object.entries(value)) {
        if (!isPositiveInteger(firstSeenAtMs)) {
            return null;
        }
        nextDedupeFirstSeenByKey[key] = firstSeenAtMs;
    }
    return nextDedupeFirstSeenByKey;
};
const parseInflightByTaskId = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const nextInflightByTaskId = {};
    for (const [taskId, inflightTask] of Object.entries(value)) {
        const parsedTask = parseInflightTask(inflightTask);
        if (!parsedTask || parsedTask.taskId !== taskId) {
            return null;
        }
        nextInflightByTaskId[taskId] = parsedTask;
    }
    return nextInflightByTaskId;
};
const parseReplayedTaskIds = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const nextReplayedTaskIds = {};
    for (const [taskId, replayed] of Object.entries(value)) {
        if (replayed !== true) {
            return null;
        }
        nextReplayedTaskIds[taskId] = true;
    }
    return nextReplayedTaskIds;
};
const parseAgentContextByLookupKey = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const nextAgentContextByLookupKey = {};
    for (const [agentContextLookupKey, rawAgentContextState] of Object.entries(value)) {
        const parsedAgentContextState = parseAgentContextState(agentContextLookupKey, rawAgentContextState);
        if (!parsedAgentContextState) {
            return null;
        }
        nextAgentContextByLookupKey[agentContextLookupKey] =
            parsedAgentContextState;
    }
    return nextAgentContextByLookupKey;
};
const parseAgentContextLifecycleCountersOrDefault = (value) => parseAgentContextLifecycleCounters(value);
const parseBootstrapExecutionByLookupKey = (value) => {
    if (value === undefined) {
        return {};
    }
    if (!isRecord(value)) {
        return null;
    }
    const nextBootstrapExecutionByLookupKey = {};
    for (const [agentContextLookupKey, executionState] of Object.entries(value)) {
        const parsedExecutionState = conversationExecutionStateSchema.safeParse(executionState);
        if (!parsedExecutionState.success) {
            return null;
        }
        if (parsedExecutionState.data.agentContextLookupKey !== agentContextLookupKey) {
            return null;
        }
        nextBootstrapExecutionByLookupKey[agentContextLookupKey] =
            parsedExecutionState.data;
    }
    return nextBootstrapExecutionByLookupKey;
};
const decodeLedgerState = (value) => {
    if (!isRecord(value)) {
        return null;
    }
    const { agentContextRotations, dedupeFirstSeenByKey, inflightByTaskId, replayedTaskIds, agentContextByLookupKey, bootstrapExecutionByLookupKey, agentContextLifecycleCounters, updatedAtMs, v, } = value;
    if ((v !== LEDGER_STATE_VERSION && v !== 1) ||
        !isPositiveInteger(updatedAtMs)) {
        return null;
    }
    const nextDedupeFirstSeenByKey = parseDedupeFirstSeenByKey(dedupeFirstSeenByKey);
    const nextInflightByTaskId = parseInflightByTaskId(inflightByTaskId);
    const nextReplayedTaskIds = parseReplayedTaskIds(replayedTaskIds);
    const nextAgentContextByLookupKey = parseAgentContextByLookupKey(agentContextByLookupKey);
    const nextBootstrapExecutionByLookupKey = parseBootstrapExecutionByLookupKey(bootstrapExecutionByLookupKey);
    const nextAgentContextLifecycleCounters = parseAgentContextLifecycleCountersOrDefault(agentContextLifecycleCounters);
    const nextAgentContextRotations = parseAgentContextRotationsOrDefault(agentContextRotations);
    if (!(nextDedupeFirstSeenByKey &&
        nextInflightByTaskId &&
        nextReplayedTaskIds &&
        nextAgentContextByLookupKey &&
        nextBootstrapExecutionByLookupKey &&
        nextAgentContextLifecycleCounters &&
        nextAgentContextRotations)) {
        return null;
    }
    return {
        dedupeFirstSeenByKey: nextDedupeFirstSeenByKey,
        inflightByTaskId: nextInflightByTaskId,
        replayedTaskIds: nextReplayedTaskIds,
        agentContextByLookupKey: nextAgentContextByLookupKey,
        bootstrapExecutionByLookupKey: nextBootstrapExecutionByLookupKey,
        agentContextLifecycleCounters: nextAgentContextLifecycleCounters,
        agentContextRotations: nextAgentContextRotations,
        updatedAtMs,
        v: LEDGER_STATE_VERSION,
    };
};
const serializeLedgerState = (state) => `${JSON.stringify(state, null, 2)}\n`;
const cloneLedgerState = (state) => ({
    dedupeFirstSeenByKey: { ...state.dedupeFirstSeenByKey },
    inflightByTaskId: Object.fromEntries(Object.entries(state.inflightByTaskId).map(([taskId, taskState]) => [
        taskId,
        {
            ...taskState,
        },
    ])),
    replayedTaskIds: { ...state.replayedTaskIds },
    agentContextByLookupKey: Object.fromEntries(Object.entries(state.agentContextByLookupKey).map(([agentContextLookupKey, sessionState]) => [
        agentContextLookupKey,
        {
            ...sessionState,
        },
    ])),
    bootstrapExecutionByLookupKey: Object.fromEntries(Object.entries(state.bootstrapExecutionByLookupKey).map(([agentContextLookupKey, executionState]) => [
        agentContextLookupKey,
        {
            ...executionState,
            ...(executionState.lastResolvedAgentControllerConversation
                ? {
                    lastResolvedAgentControllerConversation: {
                        ...executionState.lastResolvedAgentControllerConversation,
                    },
                }
                : {}),
        },
    ])),
    agentContextLifecycleCounters: {
        ...state.agentContextLifecycleCounters,
    },
    agentContextRotations: state.agentContextRotations.map((record) => ({
        ...record,
    })),
    updatedAtMs: state.updatedAtMs,
    v: LEDGER_STATE_VERSION,
});
const buildHistoryArchivePath = (historyFilePath, index) => {
    const suffix = ".ndjson";
    if (historyFilePath.endsWith(suffix)) {
        return `${historyFilePath.slice(0, -suffix.length)}.${String(index)}${suffix}`;
    }
    return `${historyFilePath}.${String(index)}`;
};
const resolveHistoryFileSize = async (input) => {
    const cachedSize = historySizeByPath.get(input.historyFilePath);
    if (typeof cachedSize === "number") {
        return cachedSize;
    }
    try {
        const content = await input.io.readFile(input.historyFilePath, UTF8_ENCODING);
        const size = utf8ByteLength(content);
        historySizeByPath.set(input.historyFilePath, size);
        return size;
    }
    catch (error) {
        if (toErrorCode(error) === "ENOENT") {
            historySizeByPath.set(input.historyFilePath, 0);
            return 0;
        }
        throw error;
    }
};
const rotateHistoryIfNeeded = async (input) => {
    const currentSize = await resolveHistoryFileSize({
        historyFilePath: input.paths.historyFilePath,
        io: input.io,
    });
    if (currentSize + input.nextLineBytes <= input.maxBytes) {
        return currentSize;
    }
    for (let index = input.maxArchives; index >= 1; index -= 1) {
        const sourcePath = index === 1
            ? input.paths.historyFilePath
            : buildHistoryArchivePath(input.paths.historyFilePath, index - 1);
        const targetPath = buildHistoryArchivePath(input.paths.historyFilePath, index);
        await input.io.rm(targetPath, { force: true }).catch(() => undefined);
        await input.io.rename(sourcePath, targetPath).catch((error) => {
            if (toErrorCode(error) === "ENOENT") {
                return;
            }
            throw error;
        });
    }
    historySizeByPath.set(input.paths.historyFilePath, 0);
    return 0;
};
export const compactLedgerState = (input) => {
    const persistedAtMs = input.nowMs ?? Date.now();
    const maxAgentContexts = typeof input.maxAgentContexts === "number" &&
        Number.isFinite(input.maxAgentContexts) &&
        input.maxAgentContexts > 0
        ? Math.trunc(input.maxAgentContexts)
        : AGENT_CONTEXT_MAX_ENTRIES;
    const referenceNowMs = resolveCompactionReferenceNowMs({
        fallbackNowMs: persistedAtMs,
        state: input.state,
    });
    const dedupeCutoffMs = referenceNowMs - DEDUPE_RETENTION_MS;
    const inflightCutoffMs = referenceNowMs - INFLIGHT_RETENTION_MS;
    const agentContextCutoffMs = referenceNowMs - AGENT_CONTEXT_RETENTION_MS;
    const nextDedupeFirstSeenByKey = Object.fromEntries(Object.entries(input.state.dedupeFirstSeenByKey).filter(([, firstSeenAtMs]) => firstSeenAtMs >= dedupeCutoffMs));
    const nextInflightByTaskId = Object.fromEntries(Object.entries(input.state.inflightByTaskId)
        .filter(([, taskState]) => taskState.firstSeenAtMs >= inflightCutoffMs)
        .map(([taskId, taskState]) => [
        taskId,
        {
            ...taskState,
        },
    ]));
    const inflightTaskIdSet = new Set(Object.keys(nextInflightByTaskId));
    const nextReplayedTaskIds = Object.fromEntries(Object.entries(input.state.replayedTaskIds).filter(([taskId, replayed]) => replayed === true && inflightTaskIdSet.has(taskId)));
    const filteredAgentContexts = Object.entries(input.state.agentContextByLookupKey)
        .filter(([, context]) => context.updatedAtMs >= agentContextCutoffMs)
        .sort((left, right) => {
        if (left[1].updatedAtMs !== right[1].updatedAtMs) {
            return right[1].updatedAtMs - left[1].updatedAtMs;
        }
        return left[0].localeCompare(right[0]);
    })
        .slice(0, maxAgentContexts);
    const nextAgentContextByLookupKey = Object.fromEntries(filteredAgentContexts);
    const nextBootstrapExecutionByLookupKey = Object.fromEntries(Object.entries(input.state.bootstrapExecutionByLookupKey).filter(([agentContextLookupKey, executionState]) => {
        if (agentContextLookupKey in nextAgentContextByLookupKey) {
            return true;
        }
        const updatedAtMs = Date.parse(executionState.updatedAt);
        return (Number.isFinite(updatedAtMs) && updatedAtMs >= agentContextCutoffMs);
    }));
    return {
        dedupeFirstSeenByKey: nextDedupeFirstSeenByKey,
        inflightByTaskId: nextInflightByTaskId,
        replayedTaskIds: nextReplayedTaskIds,
        agentContextByLookupKey: nextAgentContextByLookupKey,
        bootstrapExecutionByLookupKey: nextBootstrapExecutionByLookupKey,
        agentContextLifecycleCounters: {
            ...input.state.agentContextLifecycleCounters,
        },
        agentContextRotations: input.state.agentContextRotations.map((record) => ({
            ...record,
        })),
        updatedAtMs: persistedAtMs,
        v: LEDGER_STATE_VERSION,
    };
};
const resolveCompactionReferenceNowMs = (input) => {
    let maxObservedMs = 0;
    for (const firstSeenAtMs of Object.values(input.state.dedupeFirstSeenByKey)) {
        if (firstSeenAtMs > maxObservedMs) {
            maxObservedMs = firstSeenAtMs;
        }
    }
    for (const inflight of Object.values(input.state.inflightByTaskId)) {
        if (inflight.firstSeenAtMs > maxObservedMs) {
            maxObservedMs = inflight.firstSeenAtMs;
        }
    }
    for (const context of Object.values(input.state.agentContextByLookupKey)) {
        if (context.updatedAtMs > maxObservedMs) {
            maxObservedMs = context.updatedAtMs;
        }
    }
    for (const executionState of Object.values(input.state.bootstrapExecutionByLookupKey)) {
        const updatedAtMs = Date.parse(executionState.updatedAt);
        if (Number.isFinite(updatedAtMs) && updatedAtMs > maxObservedMs) {
            maxObservedMs = updatedAtMs;
        }
    }
    for (const rotation of input.state.agentContextRotations) {
        if (rotation.rotationAt > maxObservedMs) {
            maxObservedMs = rotation.rotationAt;
        }
    }
    return maxObservedMs > 0 ? maxObservedMs : input.fallbackNowMs;
};
const backupInvalidStateFile = async (input) => {
    const backupPath = input.paths.backupStateFilePath ?? `${input.paths.stateFilePath}.bak`;
    try {
        await input.io.copyFile(input.paths.stateFilePath, backupPath);
    }
    catch {
        throw new Error(LEDGER_STATE_BACKUP_ERROR);
    }
};
const enqueueHistoryWrite = async (input) => {
    const previous = historyWriteQueueByPath.get(input.historyFilePath) ?? EMPTY_PROMISE;
    const next = previous.catch(() => undefined).then(input.operation);
    historyWriteQueueByPath.set(input.historyFilePath, next);
    try {
        await next;
    }
    finally {
        if (historyWriteQueueByPath.get(input.historyFilePath) === next) {
            historyWriteQueueByPath.delete(input.historyFilePath);
        }
    }
};
const enqueueLedgerStateWrite = async (input) => {
    const previous = stateWriteQueueByPath.get(input.stateFilePath) ?? EMPTY_PROMISE;
    const next = previous.catch(() => undefined).then(input.operation);
    const settled = next.then(() => undefined, () => undefined);
    stateWriteQueueByPath.set(input.stateFilePath, settled);
    try {
        return await next;
    }
    finally {
        if (stateWriteQueueByPath.get(input.stateFilePath) === settled) {
            stateWriteQueueByPath.delete(input.stateFilePath);
        }
    }
};
export const createEmptyLedgerState = (nowMs = Date.now()) => ({
    dedupeFirstSeenByKey: {},
    inflightByTaskId: {},
    replayedTaskIds: {},
    agentContextByLookupKey: {},
    bootstrapExecutionByLookupKey: {},
    agentContextLifecycleCounters: createEmptyAgentContextLifecycleCounters(),
    agentContextRotations: [],
    updatedAtMs: nowMs,
    v: LEDGER_STATE_VERSION,
});
export const parseLedgerState = (value) => decodeLedgerState(value);
const loadLedgerStateUnlocked = async (input) => {
    let rawState = "";
    try {
        rawState = await input.io.readFile(input.paths.stateFilePath, UTF8_ENCODING);
    }
    catch (error) {
        if (toErrorCode(error) === "ENOENT") {
            return createEmptyLedgerState();
        }
        throw error;
    }
    let parsedUnknown;
    try {
        parsedUnknown = JSON.parse(rawState);
    }
    catch {
        await backupInvalidStateFile(input);
        return handleInvalidLedgerState({
            errorCode: LEDGER_STATE_INVALID_JSON_ERROR,
            invalidStateStrategy: input.invalidStateStrategy,
            io: input.io,
            paths: input.paths,
        });
    }
    const parsedState = parseLedgerState(parsedUnknown);
    if (!parsedState) {
        await backupInvalidStateFile(input);
        return handleInvalidLedgerState({
            errorCode: LEDGER_STATE_SCHEMA_ERROR,
            invalidStateStrategy: input.invalidStateStrategy,
            io: input.io,
            paths: input.paths,
        });
    }
    if (isRecord(parsedUnknown) && parsedUnknown.v === 1) {
        await backupPriorVersionLedgerState({ ...input, rawState });
    }
    return parsedState;
};
export const loadLedgerState = async (input) => enqueueLedgerStateWrite({
    operation: () => loadLedgerStateUnlocked(input),
    stateFilePath: input.paths.stateFilePath,
});
const backupPriorVersionLedgerState = async (input) => {
    // Keep the migration checkpoint separate from invalid-state recovery backups.
    const backupPath = `${input.paths.stateFilePath}.v1.bak`;
    let tempPath;
    try {
        let existingBackup;
        try {
            existingBackup = await input.io.readFile(backupPath, UTF8_ENCODING);
        }
        catch (error) {
            if (toErrorCode(error) !== "ENOENT") {
                throw error;
            }
        }
        if (existingBackup === undefined) {
            tempPath = buildScopedStateTempFilePath({
                stateTempFilePath: `${backupPath}.tmp`,
            });
            await input.io.copyFile(input.paths.stateFilePath, tempPath);
            await input.io.syncPath(tempPath);
            if ((await input.io.readFile(tempPath, UTF8_ENCODING)) !== input.rawState) {
                throw new Error(LEDGER_STATE_BACKUP_ERROR);
            }
            await input.io.rename(tempPath, backupPath);
            tempPath = undefined;
        }
        else if (existingBackup !== input.rawState) {
            throw new Error(LEDGER_STATE_BACKUP_ERROR);
        }
        await input.io.syncPath(backupPath);
        await input.io.syncPath(input.paths.rootDirPath);
        if ((await input.io.readFile(backupPath, UTF8_ENCODING)) !== input.rawState) {
            throw new Error(LEDGER_STATE_BACKUP_ERROR);
        }
    }
    catch (error) {
        throw new Error(LEDGER_STATE_BACKUP_ERROR, { cause: error });
    }
    finally {
        if (tempPath) {
            await input.io.rm(tempPath, { force: true }).catch(() => undefined);
        }
    }
};
const handleInvalidLedgerState = async (input) => {
    if (input.invalidStateStrategy !== "backup_and_reset") {
        throw new Error(input.errorCode);
    }
    const resetAtMs = Date.now();
    const emptyState = createEmptyLedgerState(resetAtMs);
    await persistLedgerStateAtomic({
        io: input.io,
        nowMs: resetAtMs,
        paths: input.paths,
        state: emptyState,
    });
    return emptyState;
};
const buildScopedStateTempFilePath = (input) => `${input.stateTempFilePath}.${Date.now()}.${Math.random()
    .toString(36)
    .slice(2, 10)}`;
const persistLedgerStateAtomic = async (input) => {
    const persistedAtMs = input.nowMs ?? Date.now();
    const normalizedState = compactLedgerState({
        nowMs: persistedAtMs,
        state: cloneLedgerState(input.state),
    });
    await input.io.mkdir(input.paths.rootDirPath, { recursive: true });
    const scopedTempFilePath = buildScopedStateTempFilePath({
        stateTempFilePath: input.paths.stateTempFilePath,
    });
    try {
        await input.io.writeFile(scopedTempFilePath, serializeLedgerState(normalizedState), UTF8_ENCODING);
        await input.io.syncPath(scopedTempFilePath);
        await input.io.rename(scopedTempFilePath, input.paths.stateFilePath);
        await input.io.syncPath(input.paths.rootDirPath);
    }
    catch (error) {
        await input.io.rm(scopedTempFilePath, {
            force: true,
        });
        throw error;
    }
};
export const persistLedgerState = async (input) => enqueueLedgerStateWrite({
    operation: async () => {
        // Direct persistence must not bypass validation or the v1 checkpoint.
        await loadLedgerStateUnlocked(input);
        await persistLedgerStateAtomic(input);
    },
    stateFilePath: input.paths.stateFilePath,
});
export const withLedgerStateTransaction = async (input) => enqueueLedgerStateWrite({
    operation: async () => {
        const state = await loadLedgerStateUnlocked({
            invalidStateStrategy: input.invalidStateStrategy,
            io: input.io,
            paths: input.paths,
        });
        const { nextState, value } = await input.mutate(state);
        if (nextState !== state) {
            await persistLedgerStateAtomic({
                io: input.io,
                paths: input.paths,
                state: nextState,
            });
        }
        return value;
    },
    stateFilePath: input.paths.stateFilePath,
});
export const appendLedgerHistoryEntry = async (input) => {
    await enqueueHistoryWrite({
        historyFilePath: input.paths.historyFilePath,
        operation: async () => {
            await input.io.mkdir(input.paths.rootDirPath, { recursive: true });
            const nextLine = `${JSON.stringify(input.entry)}\n`;
            const nextLineBytes = utf8ByteLength(nextLine);
            const currentSize = await rotateHistoryIfNeeded({
                io: input.io,
                maxBytes: HISTORY_ROTATION_MAX_BYTES,
                maxArchives: HISTORY_ROTATION_MAX_ARCHIVES,
                nextLineBytes,
                paths: input.paths,
            });
            await input.io.appendFile(input.paths.historyFilePath, nextLine, UTF8_ENCODING);
            historySizeByPath.set(input.paths.historyFilePath, currentSize + nextLineBytes);
        },
    });
};
export const resolveDedupeFirstSeenAt = (input) => input.state.dedupeFirstSeenByKey[input.idempotencyKey];
export const recordDedupeFirstSeenAt = (input) => {
    const current = input.state.dedupeFirstSeenByKey[input.idempotencyKey];
    if (current !== undefined && current <= input.firstSeenAtMs) {
        return input.state;
    }
    const nextState = cloneLedgerState(input.state);
    nextState.dedupeFirstSeenByKey[input.idempotencyKey] = input.firstSeenAtMs;
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const upsertInflightTask = (input) => {
    const nextState = cloneLedgerState(input.state);
    nextState.inflightByTaskId[input.task.taskId] = {
        ...input.task,
    };
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const settleInflightTask = (input) => {
    if (!(input.taskId in input.state.inflightByTaskId)) {
        return input.state;
    }
    const nextState = cloneLedgerState(input.state);
    delete nextState.inflightByTaskId[input.taskId];
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const resolveAgentContextByLookupKey = (input) => input.state.agentContextByLookupKey[input.agentContextLookupKey];
export const resolveConversationExecutionStateByLookupKey = (input) => input.state.bootstrapExecutionByLookupKey[input.agentContextLookupKey];
export const listAgentContexts = (state) => Object.values(state.agentContextByLookupKey);
export const upsertAgentContext = (input) => {
    const nextState = cloneLedgerState(input.state);
    nextState.agentContextByLookupKey[input.agentContext.agentContextLookupKey] =
        {
            ...input.agentContext,
        };
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const removeAgentContext = (input) => {
    if (!(input.agentContextLookupKey in input.state.agentContextByLookupKey)) {
        return input.state;
    }
    const nextState = cloneLedgerState(input.state);
    delete nextState.agentContextByLookupKey[input.agentContextLookupKey];
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const upsertConversationExecutionState = (input) => {
    const nextState = cloneLedgerState(input.state);
    nextState.bootstrapExecutionByLookupKey[input.executionState.agentContextLookupKey] = {
        ...input.executionState,
        ...(input.executionState.lastResolvedAgentControllerConversation
            ? {
                lastResolvedAgentControllerConversation: {
                    ...input.executionState.lastResolvedAgentControllerConversation,
                },
            }
            : {}),
    };
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const removeConversationExecutionState = (input) => {
    if (!(input.agentContextLookupKey in input.state.bootstrapExecutionByLookupKey)) {
        return input.state;
    }
    const nextState = cloneLedgerState(input.state);
    delete nextState.bootstrapExecutionByLookupKey[input.agentContextLookupKey];
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const seedLegacyAgentContext = (input) => {
    const normalizedLegacyAgentControllerConversationId = normalizeOptionalText(input.legacyAgentControllerConversationId);
    if (!normalizedLegacyAgentControllerConversationId) {
        return input.state;
    }
    if (input.state.agentContextByLookupKey[input.agentContextLookupKey]) {
        return input.state;
    }
    return upsertAgentContext({
        agentContext: {
            agentControllerConversationId: normalizedLegacyAgentControllerConversationId,
            agentControllerRef: resolveStoredAgentControllerRef(input.agentControllerRef),
            createdAtMs: input.nowMs,
            lastTaskId: input.taskId,
            agentContextLookupKey: input.agentContextLookupKey,
            runtimeWorkspaceFingerprint: input.runtimeWorkspaceFingerprint,
            spaceId: input.spaceId,
            targetProfileId: input.targetProfileId,
            transportMode: input.transportMode,
            updatedAtMs: input.nowMs,
        },
        state: input.state,
    });
};
export const commitResolvedAgentContext = (input) => {
    const normalizedAgentControllerConversationId = normalizeOptionalText(input.agentControllerConversationId);
    if (!normalizedAgentControllerConversationId) {
        return input.state;
    }
    const existing = input.state.agentContextByLookupKey[input.agentContextLookupKey] ?? null;
    return upsertAgentContext({
        agentContext: {
            agentControllerConversationId: normalizedAgentControllerConversationId,
            agentControllerRef: resolveStoredAgentControllerRef(input.agentControllerRef),
            createdAtMs: existing?.agentControllerConversationId ===
                normalizedAgentControllerConversationId
                ? existing.createdAtMs
                : input.nowMs,
            lastTaskId: input.taskId,
            agentContextLookupKey: input.agentContextLookupKey,
            runtimeWorkspaceFingerprint: input.runtimeWorkspaceFingerprint,
            spaceId: input.spaceId,
            targetProfileId: input.targetProfileId,
            transportMode: input.transportMode,
            updatedAtMs: input.nowMs,
        },
        state: input.state,
    });
};
export const bumpAgentContextLifecycleCounters = (input) => {
    const nextState = cloneLedgerState(input.state);
    for (const [key, delta] of Object.entries(input.delta)) {
        const counterKey = key;
        if (!delta || delta <= 0) {
            continue;
        }
        nextState.agentContextLifecycleCounters[counterKey] += delta;
    }
    nextState.updatedAtMs = Date.now();
    return nextState;
};
export const appendAgentContextRotationRecord = (input) => {
    const maxRecords = input.maxRecords ?? 200;
    const normalizedMaxRecords = Number.isInteger(maxRecords) && maxRecords > 0 ? maxRecords : 200;
    const nextState = cloneLedgerState(input.state);
    nextState.agentContextRotations.push({
        ...input.record,
    });
    if (nextState.agentContextRotations.length > normalizedMaxRecords) {
        nextState.agentContextRotations = nextState.agentContextRotations.slice(-normalizedMaxRecords);
    }
    nextState.updatedAtMs = Date.now();
    return nextState;
};
function normalizeOptionalText(value) {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
}
function resolveStoredAgentControllerRef(agentControllerRef) {
    const canonicalAgentControllerRef = canonicalizeAgentControllerRef(agentControllerRef);
    if (!canonicalAgentControllerRef) {
        throw new Error(`ledger.agent_context.invalid_agent_controller_ref: ${agentControllerRef}`);
    }
    return canonicalAgentControllerRef;
}
